import { type FC, useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, Button, Input, Label } from '@/components/ui';
import { Check, Clock, FileSignature, Loader2, ExternalLink, FileText } from 'lucide-react';
import { useWaiverSettings } from '@/hooks/config/useWaiverSettings';
import { useAuth } from '@/hooks';
import type { DayPass } from '@/hooks/config/useEventRegistrationSettings';
import { DayPassSelector } from './DayPassSelector';
import {
  coveredDayPassIds,
  hasFullWeekendCoverage,
  toggleDayPassSelection,
} from '../utils/registrationEntryState';

export type EntrySelection = 'free' | 'spectator' | 'paid' | 'day_pass';

interface EventEntryStepProps {
  onComplete: (data: {
    wantsToPay: boolean;
    entryType: EntrySelection;
    entryFee: number;
    dayPassIds?: string[];
    entryName?: string;
  }) => void;
  fee: number; // in cents - the active fee to charge (early bird or regular)
  regularFee: number; // in cents - the regular price (for showing struck-through when early bird active)
  entryName: string;
  entryDescription: string;
  // When false, the paid "supporter" entry tier is hidden (new paid registrations closed)
  paidEntryEnabled: boolean;
  // Per-day attendance passes OFFERED FOR SALE (rendered as selectable tiers). Empty when
  // day_passes_enabled is false: a closed product must never be offered.
  dayPasses: DayPass[];
  // Every CONFIGURED day pass, whether or not the tier is still on sale. Deliberately not
  // the same list as `dayPasses`, and not a candidate for "simplification" into it: this
  // one answers "what does the buyer ALREADY OWN?", which is a question about the passes
  // in their account, not about what is still for sale. Closing day-pass sales does not
  // un-sell the Full Weekend Pass someone bought last month. See the coverage check below.
  allDayPasses: DayPass[];
  // Day passes the user already holds (their ids); rendered disabled with a
  // "Purchased" badge. Also switches the step into "add days / upgrade" mode.
  ownedDayPassIds?: string[];
  freeEntryEnabled: boolean;
  freeEntryName: string;
  freeEntryDescription: string;
  freeEntryBenefits: string[];
  paidEntryBenefits: string[];
  spectatorPassEnabled: boolean;
  spectatorPassFee: number;
  spectatorPassName: string;
  spectatorPassDescription: string;
  spectatorPassBenefits: string[];
  isEarlyBird: boolean;
  earlyBirdEndDate: string | null;
  waiverUrl: string;
}

/**
 * First step of registration wizard - Event entry selection
 * Allows users to choose between free entry or paid supporter entry
 */
export const EventEntryStep: FC<EventEntryStepProps> = ({
  onComplete,
  fee,
  regularFee,
  entryName,
  entryDescription,
  paidEntryEnabled,
  dayPasses,
  allDayPasses,
  ownedDayPassIds = [],
  freeEntryEnabled,
  freeEntryName,
  freeEntryDescription,
  freeEntryBenefits,
  paidEntryBenefits,
  spectatorPassEnabled,
  spectatorPassFee,
  spectatorPassName,
  spectatorPassDescription,
  spectatorPassBenefits,
  isEarlyBird,
  earlyBirdEndDate,
  waiverUrl,
}) => {
  const { user } = useAuth();
  const {
    settings: waiverSettings,
    isLoading: isWaiverLoading,
    waiversWithStatus,
    allWaiversSigned,
    pendingWaivers,
    createWaiver,
    refreshStatus,
  } = useWaiverSettings(2026);

  // The spectator tier is a FULL-WEEKEND product at the same price as the weekend day
  // pass. A user whose owned passes already cover every day would be paying twice for
  // identical access, and nothing downstream would catch it: a weekend day pass records
  // day_pass_id='weekend', so it is not a "weekend registration". Hide the tier from them.
  // Only the spectator tier — the supporter tier carries extra benefits, so buying it on
  // top is a real upgrade, and so is a spectator pass for someone who owns only one day.
  //
  // Note this reads allDayPasses, NOT the sale list `dayPasses`. Whether day passes are
  // still ON SALE has nothing to do with whether a pass the buyer ALREADY OWNS covers the
  // weekend. Feed it the sale list and closing day-pass sales (day_passes_enabled=false,
  // day_passes JSON left in place) empties it, coverage collapses to false, and a weekend
  // -pass holder is re-offered the same $30 access — which create-checkout-session, which
  // reads the ungated config, then refuses: an error for an item the UI just offered.
  const spectatorAvailable =
    spectatorPassEnabled && !hasFullWeekendCoverage(allDayPasses, ownedDayPassIds);

  const [selection, setSelection] = useState<EntrySelection | null>(null);
  // When selection === 'day_pass', the ids of every chosen day pass
  const [selectedDayPassIds, setSelectedDayPassIds] = useState<string[]>([]);
  // Which branch of the spectator section is showing when day passes exist.
  // Returning day-pass holders land on "by the day" since that's their tier.
  const [spectatorView, setSpectatorView] = useState<'weekend' | 'by_day'>(
    spectatorAvailable && ownedDayPassIds.length === 0 ? 'weekend' : 'by_day'
  );
  const [liabilityWaiverAccepted, setLiabilityWaiverAccepted] = useState(false);

  // BoldSign waiver state - now tracks per-waiver
  const [signerName, setSignerName] = useState('');
  const [signerEmail, setSignerEmail] = useState('');
  const [isCreatingWaiver, setIsCreatingWaiver] = useState<string | null>(null); // template ID being created
  const [activeWaiverIframe, setActiveWaiverIframe] = useState<string | null>(null); // template ID showing iframe
  const [waiverSignUrls, setWaiverSignUrls] = useState<Map<string, string>>(new Map());
  const [waiverError, setWaiverError] = useState<string | null>(null);

  // Check if we have multiple waivers configured
  const hasMultipleWaivers = waiversWithStatus.length > 0;
  const useBoldSign = hasMultipleWaivers || (waiverSettings.boldsignEnabled && waiverSettings.boldsignTemplateId);

  // Pre-fill email from user
  useEffect(() => {
    if (user?.email) {
      setSignerEmail(user.email);
    }
  }, [user]);

  // Auto-accept waiver checkbox if all waivers are signed
  useEffect(() => {
    if (allWaiversSigned) {
      setLiabilityWaiverAccepted(true);
    }
  }, [allWaiversSigned]);

  // Weekend tiers and day passes are mutually exclusive within one purchase:
  // picking any weekend tier clears the day selection, and vice versa.
  const selectTier = (tier: EntrySelection): void => {
    setSelection(selection === tier ? null : tier);
    setSelectedDayPassIds([]);
  };

  // Day-pass ids already covered by a selected/owned bundle pass (e.g. the weekend
  // pass covers Friday/Saturday). Hoisted above the handler so both the guard and
  // the <DayPassSelector coveredIds> prop share a single computation.
  const coveredIds = coveredDayPassIds(dayPasses, selectedDayPassIds, ownedDayPassIds);

  const handleToggleDayPass = (passId: string): void => {
    if (ownedDayPassIds.includes(passId)) return;
    if (coveredIds.includes(passId)) return;
    const next = toggleDayPassSelection(selectedDayPassIds, passId, dayPasses);
    setSelectedDayPassIds(next);
    setSelection(next.length > 0 ? 'day_pass' : null);
  };

  // Handle BoldSign waiver creation for a specific template
  const handleSignWaiver = useCallback(async (templateId: string) => {
    if (!signerName.trim()) {
      setWaiverError('Please enter your full legal name');
      return;
    }
    if (!signerEmail.trim()) {
      setWaiverError('Please enter your email address');
      return;
    }

    setWaiverError(null);
    setIsCreatingWaiver(templateId);

    try {
      const result = await createWaiver(signerName.trim(), signerEmail.trim(), templateId);

      if (result?.signUrl) {
        setWaiverSignUrls(prev => new Map(prev).set(templateId, result.signUrl));
        setActiveWaiverIframe(templateId);
      }
    } catch (err) {
      setWaiverError('Failed to create waiver. Please try again.');
    } finally {
      setIsCreatingWaiver(null);
    }
  }, [signerName, signerEmail, createWaiver]);

  // Poll for waiver status when iframe is showing
  useEffect(() => {
    if (!activeWaiverIframe) return;

    const interval = setInterval(async () => {
      await refreshStatus();
      // Check if the active waiver is now signed
      const activeWaiver = waiversWithStatus.find(w => w.template.id === activeWaiverIframe);
      if (activeWaiver?.isValid) {
        setActiveWaiverIframe(null);
        setWaiverSignUrls(prev => {
          const next = new Map(prev);
          next.delete(activeWaiverIframe);
          return next;
        });
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [activeWaiverIframe, refreshStatus, waiversWithStatus]);

  // Determine waiver completion status
  // If waiver is not required, always consider it completed
  const waiverCompleted = !waiverSettings.waiverRequired || (hasMultipleWaivers
    ? allWaiversSigned
    : (useBoldSign ? allWaiversSigned : liabilityWaiverAccepted));

  const selectedDayPasses = dayPasses.filter((p) => selectedDayPassIds.includes(p.id));

  const handleContinue = (): void => {
    if (!selection || !waiverCompleted) return;
    // Day pass requires at least one specific day to be chosen
    if (selection === 'day_pass' && selectedDayPasses.length === 0) return;

    let entryFee = 0;
    let chosenName: string | undefined;
    let dayPassIds: string[] | undefined;

    if (selection === 'spectator') {
      entryFee = spectatorPassFee;
      chosenName = spectatorPassName;
    } else if (selection === 'paid') {
      entryFee = fee;
      chosenName = entryName;
    } else if (selection === 'day_pass') {
      entryFee = selectedDayPasses.reduce((sum, p) => sum + p.fee, 0);
      chosenName = selectedDayPasses.map((p) => p.name).join(' + ');
      dayPassIds = selectedDayPasses.map((p) => p.id);
    }

    onComplete({
      wantsToPay: selection === 'paid' || selection === 'spectator' || selection === 'day_pass',
      entryType: selection,
      entryFee,
      ...(dayPassIds && { dayPassIds }),
      ...(chosenName && { entryName: chosenName }),
    });
  };

  const feeInDollars = fee / 100;
  const regularFeeInDollars = regularFee / 100;
  const spectatorFeeInDollars = spectatorPassFee / 100;
  const isFreeEvent = fee === 0;

  // Format the early bird end date for display
  const formatEarlyBirdEndDate = (dateStr: string | null): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <Card className="backdrop-blur-md bg-turquoise-800/90 border-2 border-orange-500">
      <CardHeader>
        <CardTitle className="font-viking text-2xl text-white">Welcome to Cactus Cup 2026!</CardTitle>
        <p className="text-orange-200">
          Join us for Arizona's hottest summer HEMA tournament and workshop event
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="prose prose-sm max-w-none">
          <p className="text-orange-100">
            {freeEntryEnabled
              ? `Thank you for your interest in Cactus Cup 2026! The event is ${isFreeEvent ? 'free to attend' : 'open to all'}, but we appreciate any contributions to help cover costs.`
              : `Thank you for your interest in Cactus Cup 2026! Please complete your registration by selecting the entry option below.`}
          </p>
        </div>

        <div
          className="flex flex-col gap-6 max-w-lg mx-auto"
          role="radiogroup"
          aria-label="Event entry options"
        >
          {/* Paid Entry Option (Top - Primary) */}
          {!isFreeEvent && paidEntryEnabled && (
            <button
              onClick={() => selectTier('paid')}
              className={`
                group relative text-left transition-all duration-300 transform hover:scale-[1.02]
                ${selection === 'paid' ? 'scale-[1.02]' : ''}
              `}
              role="radio"
              aria-checked={selection === 'paid'}
              aria-label={`${entryName} - $${feeInDollars} paid entry option${isEarlyBird ? ' (Early bird pricing)' : ''}`}
            >
              {/* Ticket shape with notches */}
              <div className={`
                relative rounded-xl overflow-hidden
                ${selection === 'paid'
                  ? 'shadow-[0_0_30px_rgba(249,115,22,0.5)] ring-2 ring-orange-500'
                  : 'shadow-lg hover:shadow-[0_0_20px_rgba(20,184,166,0.3)]'}
              `}>
                {/* Top section */}
                <div className={`
                  relative p-6 pb-4 bg-gradient-to-br
                  ${selection === 'paid'
                    ? 'from-orange-500 to-orange-600'
                    : 'from-turquoise-600 to-turquoise-700 group-hover:from-turquoise-500 group-hover:to-turquoise-600'}
                `}>
                  {/* Early Bird Badge */}
                  {isEarlyBird && (
                    <div className="mb-3">
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold shadow-md animate-pulse ${selection === 'paid' ? 'bg-green-500/90 text-white' : 'bg-green-500/90 text-white'}`}>
                        <Clock className="h-3 w-3" />
                        Early Bird
                      </span>
                    </div>
                  )}

                  {/* Stylized checkbox indicator */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`
                      relative h-6 w-6 rounded-md flex items-center justify-center transition-all duration-300
                      ${selection === 'paid'
                        ? 'bg-turquoise-500 shadow-[0_0_10px_rgba(20,184,166,0.5)]'
                        : 'bg-turquoise-800/50 border-2 border-orange-400 group-hover:border-orange-300 group-hover:shadow-[0_0_8px_rgba(249,115,22,0.3)]'}
                    `}>
                      {selection === 'paid' && <Check className="h-4 w-4 text-white" strokeWidth={3} />}
                    </div>
                    <span className={`
                      text-sm font-semibold uppercase tracking-wider transition-colors duration-300
                      ${selection === 'paid' ? 'text-turquoise-100' : 'text-orange-200 group-hover:text-orange-100'}
                    `}>
                      {selection === 'paid' ? 'Selected' : 'Click to select'}
                    </span>
                  </div>

                  <div>
                    <h3 className={`text-xl font-bold ${selection === 'paid' ? 'text-turquoise-100' : 'text-white'}`}>{entryName}</h3>
                    <div className="mt-2">
                      {isEarlyBird ? (
                        <div className="flex items-baseline gap-3">
                          <p className={`text-4xl font-black ${selection === 'paid' ? 'text-green-400' : 'text-green-400'}`}>${feeInDollars}</p>
                          <p className={`text-xl line-through ${selection === 'paid' ? 'text-turquoise-300/60' : 'text-orange-300/60'}`}>${regularFeeInDollars}</p>
                          <span className={`px-2 py-0.5 rounded text-xs font-bold ${selection === 'paid' ? 'bg-green-500/20 text-green-300' : 'bg-green-500/20 text-green-300'}`}>
                            SAVE ${regularFeeInDollars - feeInDollars}
                          </span>
                        </div>
                      ) : (
                        <p className={`text-4xl font-black ${selection === 'paid' ? 'text-turquoise-100' : 'text-orange-400'}`}>${feeInDollars}</p>
                      )}
                    </div>
                  </div>

                  {/* Early bird countdown */}
                  {isEarlyBird && earlyBirdEndDate && (
                    <div className={`mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border ${selection === 'paid' ? 'bg-green-500/20 border-green-500/30' : 'bg-green-500/20 border-green-500/30'}`}>
                      <Clock className="h-4 w-4 text-green-400" />
                      <span className="text-sm font-medium text-green-300">Ends {formatEarlyBirdEndDate(earlyBirdEndDate)}</span>
                    </div>
                  )}
                </div>

                {/* Perforated divider */}
                <div className={`relative h-4 ${selection === 'paid' ? 'bg-orange-700/50' : 'bg-turquoise-800/50'}`}>
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-turquoise-800"></div>
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-8 h-8 rounded-full bg-turquoise-800"></div>
                  <div className={`absolute inset-x-8 top-1/2 border-t-2 border-dashed ${selection === 'paid' ? 'border-orange-400/50' : 'border-turquoise-600/50'}`}></div>
                </div>

                {/* Bottom section */}
                <div className={`
                  relative p-6 pt-4
                  ${selection === 'paid'
                    ? 'bg-orange-600/90'
                    : 'bg-turquoise-700/80'}
                `}>
                  <p className={`text-sm mb-4 ${selection === 'paid' ? 'text-turquoise-100' : 'text-orange-200'}`}>
                    {entryDescription}
                  </p>
                  <ul className={`space-y-2 text-sm ${selection === 'paid' ? 'text-turquoise-100' : 'text-orange-100'}`}>
                    {paidEntryBenefits.map((benefit, index) => (
                      <li key={index} className="flex items-start">
                        <Check className={`h-4 w-4 mr-2 mt-0.5 flex-shrink-0 ${selection === 'paid' ? 'text-turquoise-300' : 'text-orange-400'}`} />
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </button>
          )}

          {/* Spectator options: full-weekend pass and/or per-day passes */}
          {(spectatorAvailable || dayPasses.length > 0) && (
            <div className="space-y-4">
              {ownedDayPassIds.length > 0 && (
                <div className="bg-turquoise-700/50 rounded-lg p-4 border border-turquoise-500">
                  {/* Names the passes the buyer OWNS, so it reads the full catalogue too:
                      the sale list is empty once day passes close, and this line would
                      otherwise render "You already have: " with nothing after it. */}
                  <p className="text-sm font-semibold text-orange-200">
                    You already have: {allDayPasses.filter((p) => ownedDayPassIds.includes(p.id)).map((p) => p.name).join(', ')}
                  </p>
                  <p className="text-xs text-orange-200/80 mt-1">
                    Add passes for other days below, or upgrade to a full event entry at full price.
                  </p>
                </div>
              )}

              {/* Branch toggle — only when both choices exist */}
              {spectatorAvailable && dayPasses.length > 0 && (
                <div className="flex rounded-lg overflow-hidden border border-turquoise-500" role="tablist" aria-label="Spectator pass options">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={spectatorView === 'weekend'}
                    onClick={() => {
                      setSpectatorView('weekend');
                      setSelectedDayPassIds([]);
                      if (selection === 'day_pass') setSelection(null);
                    }}
                    className={`flex-1 px-4 py-2 text-sm font-semibold transition-colors ${
                      spectatorView === 'weekend'
                        ? 'bg-orange-500 text-white'
                        : 'bg-turquoise-700/50 text-orange-200 hover:bg-turquoise-600/50'
                    }`}
                  >
                    Full weekend
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={spectatorView === 'by_day'}
                    onClick={() => {
                      setSpectatorView('by_day');
                      if (selection === 'spectator') setSelection(null);
                    }}
                    className={`flex-1 px-4 py-2 text-sm font-semibold transition-colors ${
                      spectatorView === 'by_day'
                        ? 'bg-orange-500 text-white'
                        : 'bg-turquoise-700/50 text-orange-200 hover:bg-turquoise-600/50'
                    }`}
                  >
                    By the day
                  </button>
                </div>
              )}

              {/* Full-weekend spectator ticket */}
              {spectatorAvailable && (dayPasses.length === 0 || spectatorView === 'weekend') && (
                <button
                  onClick={() => selectTier('spectator')}
                  className={`
                    group relative text-left transition-all duration-300 transform hover:scale-[1.02]
                    ${selection === 'spectator' ? 'scale-[1.02]' : ''}
                  `}
                  role="radio"
                  aria-checked={selection === 'spectator'}
                  aria-label={`${spectatorPassName} - $${spectatorFeeInDollars} spectator pass option`}
                >
                  {/* Ticket shape with notches */}
                  <div className={`
                    relative rounded-xl overflow-hidden
                    ${selection === 'spectator'
                      ? 'shadow-[0_0_30px_rgba(249,115,22,0.5)] ring-2 ring-orange-500'
                      : 'shadow-lg hover:shadow-[0_0_20px_rgba(20,184,166,0.3)]'}
                  `}>
                    {/* Top section */}
                    <div className={`
                      p-6 pb-4 bg-gradient-to-br
                      ${selection === 'spectator'
                        ? 'from-orange-500 to-orange-600'
                        : 'from-turquoise-600 to-turquoise-700 group-hover:from-turquoise-500 group-hover:to-turquoise-600'}
                    `}>
                      {/* Stylized checkbox indicator */}
                      <div className="flex items-center gap-3 mb-4">
                        <div className={`
                          relative h-6 w-6 rounded-md flex items-center justify-center transition-all duration-300
                          ${selection === 'spectator'
                            ? 'bg-turquoise-500 shadow-[0_0_10px_rgba(20,184,166,0.5)]'
                            : 'bg-turquoise-800/50 border-2 border-orange-400 group-hover:border-orange-300 group-hover:shadow-[0_0_8px_rgba(249,115,22,0.3)]'}
                        `}>
                          {selection === 'spectator' && <Check className="h-4 w-4 text-white" strokeWidth={3} />}
                        </div>
                        <span className={`
                          text-sm font-semibold uppercase tracking-wider transition-colors duration-300
                          ${selection === 'spectator' ? 'text-turquoise-100' : 'text-orange-200 group-hover:text-orange-100'}
                        `}>
                          {selection === 'spectator' ? 'Selected' : 'Click to select'}
                        </span>
                      </div>

                      <div>
                        <h3 className={`text-xl font-bold ${selection === 'spectator' ? 'text-turquoise-100' : 'text-white'}`}>{spectatorPassName}</h3>
                        <p className={`text-4xl font-black mt-2 ${selection === 'spectator' ? 'text-turquoise-100' : 'text-orange-400'}`}>${spectatorFeeInDollars}</p>
                      </div>
                    </div>

                    {/* Perforated divider */}
                    <div className={`relative h-4 ${selection === 'spectator' ? 'bg-orange-700/50' : 'bg-turquoise-800/50'}`}>
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-turquoise-800"></div>
                      <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-8 h-8 rounded-full bg-turquoise-800"></div>
                      <div className={`absolute inset-x-8 top-1/2 border-t-2 border-dashed ${selection === 'spectator' ? 'border-orange-400/50' : 'border-turquoise-600/50'}`}></div>
                    </div>

                    {/* Bottom section */}
                    <div className={`
                      p-6 pt-4
                      ${selection === 'spectator'
                        ? 'bg-orange-600/90'
                        : 'bg-turquoise-700/80'}
                    `}>
                      <p className={`text-sm mb-4 ${selection === 'spectator' ? 'text-turquoise-100' : 'text-orange-200'}`}>
                        {spectatorPassDescription}
                      </p>
                      <ul className={`space-y-2 text-sm ${selection === 'spectator' ? 'text-turquoise-100' : 'text-orange-100'}`}>
                        {spectatorPassBenefits.map((benefit, index) => (
                          <li key={index} className="flex items-start">
                            <Check className={`h-4 w-4 mr-2 mt-0.5 flex-shrink-0 ${selection === 'spectator' ? 'text-turquoise-300' : 'text-orange-400'}`} />
                            <span>{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </button>
              )}

              {/* Per-day passes */}
              {dayPasses.length > 0 && (!spectatorAvailable || spectatorView === 'by_day') && (
                <DayPassSelector
                  passes={dayPasses}
                  ownedIds={ownedDayPassIds}
                  coveredIds={coveredIds}
                  selectedIds={selectedDayPassIds}
                  onToggle={handleToggleDayPass}
                />
              )}
            </div>
          )}

          {/* Free entry is a weekend tier — day-pass holders upgrade to paid tiers instead */}
          {freeEntryEnabled && ownedDayPassIds.length === 0 && (
            <button
              onClick={() => selectTier('free')}
              className={`
                group relative text-left transition-all duration-300 transform hover:scale-[1.02]
                ${selection === 'free' ? 'scale-[1.02]' : ''}
              `}
              role="radio"
              aria-checked={selection === 'free'}
              aria-label={`${freeEntryName} - Free entry option`}
            >
              {/* Ticket shape with notches */}
              <div className={`
                relative rounded-xl overflow-hidden
                ${selection === 'free'
                  ? 'shadow-[0_0_30px_rgba(249,115,22,0.5)] ring-2 ring-orange-500'
                  : 'shadow-lg hover:shadow-[0_0_20px_rgba(20,184,166,0.3)]'}
              `}>
                {/* Top section */}
                <div className={`
                  p-6 pb-4 bg-gradient-to-br
                  ${selection === 'free'
                    ? 'from-orange-500 to-orange-600'
                    : 'from-turquoise-600 to-turquoise-700 group-hover:from-turquoise-500 group-hover:to-turquoise-600'}
                `}>
                  {/* Stylized checkbox indicator */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`
                      relative h-6 w-6 rounded-md flex items-center justify-center transition-all duration-300
                      ${selection === 'free'
                        ? 'bg-turquoise-500 shadow-[0_0_10px_rgba(20,184,166,0.5)]'
                        : 'bg-turquoise-800/50 border-2 border-orange-400 group-hover:border-orange-300 group-hover:shadow-[0_0_8px_rgba(249,115,22,0.3)]'}
                    `}>
                      {selection === 'free' && <Check className="h-4 w-4 text-white" strokeWidth={3} />}
                    </div>
                    <span className={`
                      text-sm font-semibold uppercase tracking-wider transition-colors duration-300
                      ${selection === 'free' ? 'text-turquoise-100' : 'text-orange-200 group-hover:text-orange-100'}
                    `}>
                      {selection === 'free' ? 'Selected' : 'Click to select'}
                    </span>
                  </div>

                  <div>
                    <h3 className={`text-xl font-bold ${selection === 'free' ? 'text-turquoise-100' : 'text-white'}`}>{freeEntryName}</h3>
                    <p className={`text-4xl font-black mt-2 ${selection === 'free' ? 'text-turquoise-100' : 'text-orange-400'}`}>$0</p>
                  </div>
                </div>

                {/* Perforated divider */}
                <div className={`relative h-4 ${selection === 'free' ? 'bg-orange-700/50' : 'bg-turquoise-800/50'}`}>
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-turquoise-800"></div>
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-8 h-8 rounded-full bg-turquoise-800"></div>
                  <div className={`absolute inset-x-8 top-1/2 border-t-2 border-dashed ${selection === 'free' ? 'border-orange-400/50' : 'border-turquoise-600/50'}`}></div>
                </div>

                {/* Bottom section */}
                <div className={`
                  p-6 pt-4
                  ${selection === 'free'
                    ? 'bg-orange-600/90'
                    : 'bg-turquoise-700/80'}
                `}>
                  <p className={`text-sm mb-4 ${selection === 'free' ? 'text-turquoise-100' : 'text-orange-200'}`}>
                    {freeEntryDescription}
                  </p>
                  <ul className={`space-y-2 text-sm ${selection === 'free' ? 'text-turquoise-100' : 'text-orange-100'}`}>
                    {freeEntryBenefits.map((benefit, index) => (
                      <li key={index} className="flex items-start">
                        <Check className={`h-4 w-4 mr-2 mt-0.5 flex-shrink-0 ${selection === 'free' ? 'text-turquoise-300' : 'text-orange-400'}`} />
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </button>
          )}
        </div>

        {/* Acknowledgments - only show when waiver is required */}
        {waiverSettings.waiverRequired && (
        <div className="space-y-4 border-t border-turquoise-600 pt-6">
          <h3 className="font-semibold text-lg mb-4 text-white" id="acknowledgments-heading">
            Required Acknowledgments
          </h3>

          <div className="space-y-4" role="group" aria-labelledby="acknowledgments-heading" aria-required={true}>
            {/* Multiple Waivers - BoldSign Digital Signature Mode */}
            {hasMultipleWaivers ? (
              <>
                {/* Signer Info - shared across all waivers */}
                {pendingWaivers.length > 0 && (
                  <div className="bg-turquoise-700/50 rounded-lg p-4 border border-turquoise-500 mb-4">
                    <p className="text-sm text-orange-200 mb-3">
                      Please enter your details to sign the required waivers ({waiversWithStatus.length - pendingWaivers.length} of {waiversWithStatus.length} completed)
                    </p>
                    {waiverError && (
                      <div className="text-sm text-red-400 bg-red-500/20 rounded px-3 py-2 mb-3">
                        {waiverError}
                      </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="signerName" className="text-sm text-orange-100">
                          Full Legal Name <span className="text-red-400">*</span>
                        </Label>
                        <Input
                          id="signerName"
                          value={signerName}
                          onChange={(e) => setSignerName(e.target.value)}
                          placeholder="Your full legal name"
                          className="mt-1 bg-turquoise-600 border-turquoise-500 text-white placeholder:text-turquoise-400 h-9"
                        />
                      </div>
                      <div>
                        <Label htmlFor="signerEmail" className="text-sm text-orange-100">
                          Email <span className="text-red-400">*</span>
                        </Label>
                        <Input
                          id="signerEmail"
                          type="email"
                          value={signerEmail}
                          onChange={(e) => setSignerEmail(e.target.value)}
                          placeholder="your@email.com"
                          className="mt-1 bg-turquoise-600 border-turquoise-500 text-white placeholder:text-turquoise-400 h-9"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Individual Waiver Cards */}
                {waiversWithStatus.map((waiver) => {
                  const signUrl = waiverSignUrls.get(waiver.template.id);
                  const isActive = activeWaiverIframe === waiver.template.id;
                  const isCreating = isCreatingWaiver === waiver.template.id;

                  return (
                    <div
                      key={waiver.template.id}
                      className={`bg-turquoise-700/50 rounded-lg p-4 border ${
                        waiver.isValid ? 'border-green-500/50' : 'border-turquoise-500'
                      }`}
                    >
                      <div className="flex items-start gap-3 mb-3">
                        {waiver.isValid ? (
                          <Check className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
                        ) : (
                          <FileSignature className="h-5 w-5 text-orange-400 flex-shrink-0 mt-0.5" />
                        )}
                        <div className="flex-1">
                          <h4 className="font-medium text-white">{waiver.template.title}</h4>
                          {waiver.template.description && (
                            <p className="text-sm text-orange-200 mt-1">{waiver.template.description}</p>
                          )}
                          {waiver.template.pdfUrl && (
                            <a
                              href={waiver.template.pdfUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-orange-400 hover:underline inline-flex items-center gap-1 mt-1"
                            >
                              <FileText className="h-3 w-3" />
                              View PDF
                            </a>
                          )}
                        </div>
                      </div>

                      {waiver.isValid ? (
                        <div className="flex items-center gap-2 text-green-400 bg-green-500/20 rounded-lg px-3 py-2">
                          <Check className="h-4 w-4" />
                          <span className="text-sm font-medium">
                            Signed {waiver.signingStatus?.signedAt && `on ${new Date(waiver.signingStatus.signedAt).toLocaleDateString()}`}
                          </span>
                        </div>
                      ) : isActive && signUrl ? (
                        <div className="space-y-3">
                          <div className="rounded-lg overflow-hidden border border-turquoise-500">
                            <iframe
                              src={signUrl}
                              width="100%"
                              height="500"
                              frameBorder="0"
                              allow="camera"
                              title={`Sign ${waiver.template.title}`}
                              className="bg-white"
                            />
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <button
                              onClick={() => setActiveWaiverIframe(null)}
                              className="text-orange-300 hover:text-orange-200"
                            >
                              Cancel
                            </button>
                            <a
                              href={signUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-orange-300 hover:text-orange-200 flex items-center gap-1"
                            >
                              Open in new tab <ExternalLink className="h-3 w-3" />
                            </a>
                          </div>
                        </div>
                      ) : (
                        <Button
                          type="button"
                          onClick={() => handleSignWaiver(waiver.template.id)}
                          disabled={isCreating || isCreatingWaiver !== null || !signerName.trim() || !signerEmail.trim()}
                          variant="outline"
                          size="sm"
                          className="w-full border-orange-500 text-orange-200 hover:bg-orange-500/20"
                        >
                          {isCreating ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Preparing Waiver...
                            </>
                          ) : (
                            <>
                              <FileSignature className="h-4 w-4 mr-2" />
                              Sign {waiver.template.title}
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  );
                })}
              </>
            ) : useBoldSign ? (
              // Legacy single BoldSign waiver mode
              <div className="bg-turquoise-700/50 rounded-lg p-4 border border-turquoise-500">
                <div className="flex items-start gap-3 mb-3">
                  <FileSignature className="h-5 w-5 text-orange-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-medium text-white">{waiverSettings.waiverTitle}</h4>
                    <p className="text-sm text-orange-200 mt-1">{waiverSettings.waiverDescription}</p>
                  </div>
                </div>
                <p className="text-sm text-orange-300">No waiver templates configured. Please contact administrator.</p>
              </div>
            ) : (
              // Checkbox Mode (Original fallback)
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={liabilityWaiverAccepted}
                  onChange={(e) => setLiabilityWaiverAccepted(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-turquoise-500 text-orange-500 focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 focus:ring-offset-turquoise-800 cursor-pointer bg-turquoise-700"
                  aria-label="I have read and agree to the Liability Consent Waiver"
                  aria-required={waiverSettings.waiverRequired}
                  id="liability-waiver-checkbox"
                />
                <span className="text-sm flex-1 text-orange-100">
                  I have read and agree to the{' '}
                  {waiverUrl || waiverSettings.waiverFallbackUrl ? (
                    <a
                      href={waiverUrl || waiverSettings.waiverFallbackUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-orange-400 hover:underline font-medium focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 focus:ring-offset-turquoise-800 rounded"
                      onClick={(e) => e.stopPropagation()}
                      aria-label="Liability Consent Waiver (opens in new tab)"
                    >
                      Liability Consent Waiver
                    </a>
                  ) : (
                    <span className="font-medium">Liability Consent Waiver</span>
                  )}
                  {waiverSettings.waiverRequired ? ' (required)' : ' (optional)'}
                </span>
              </label>
            )}

          </div>

          {/* Error message when trying to continue without accepting */}
          {selection && !waiverCompleted && (
            <p className="text-sm text-red-400 mt-2" role="alert" aria-live="polite">
              {!waiverCompleted && hasMultipleWaivers
                ? `Please sign all required waivers to continue (${pendingWaivers.length} remaining)`
                : !waiverCompleted && useBoldSign
                  ? 'Please sign the waiver to continue'
                  : 'Please accept all required acknowledgments to continue'}
            </p>
          )}
        </div>
        )}

        <div className="flex justify-end pt-4">
          <Button
            onClick={handleContinue}
            disabled={!selection || !waiverCompleted || isWaiverLoading}
            size="lg"
            variant="secondary"
            className="min-w-[200px]"
            aria-label="Continue to tournament selection"
          >
            Continue to Next Steps
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
